import React, { useEffect, useMemo } from "react";

import styled from "styled-components";

import { usePermissions } from "Flocknote/apiHooks";

import NavLink from "NavBar/components/NavLink";
import NavLinksWrapper from "NavBar/components/NavLinksWrapper";
import NavSection from "NavBar/components/NavSection";
import {
  navSectionKeys,
  useActiveSectionKey,
  useHomePageLinkRef,
  useMinistryToolsRef,
  usePrayerRequestsLinkRef,
  useReportBuilderLinkRef,
} from "NavBar/navBarStore";
import {
  useIsHomePageLinkFlashing,
  useIsMinistryToolsSquiggleFlashing,
  useIsPrayerRequestsLinkFlashing,
  useIsReportBuilderLinkFlashing,
} from "NavBar/stateHooks";
import { useGetPrayerRequestStats } from "PrayerRequests/API/queries";

import { navColors } from "UI/colors";

import { useUnseenPrayerRequestsStore } from "./useUnseenPrayerRequestsStore";

const MinistryTools = () => {
  const [activeSectionKey] = useActiveSectionKey();
  const isActive = activeSectionKey === navSectionKeys.tools;

  const [
    {
      canSeePrayerPortalNav,
      canSeeReportsMenuNav,
      canManageHomePage,
      canSeeMMGSetup,
    },
  ] = usePermissions();
  const { data: { unseenTotal: initialUnseenTotal } = {} } =
    useGetPrayerRequestStats();

  const { unseenTotal, initialized, initialize } =
    useUnseenPrayerRequestsStore();

  const ministryToolsRef = useMinistryToolsRef();
  const reportBuilderLinkRef = useReportBuilderLinkRef();
  const prayerRequestsLinkRef = usePrayerRequestsLinkRef();
  const homePageLinkRef = useHomePageLinkRef();
  const isReportBuilderFlashing = useIsReportBuilderLinkFlashing();
  const isPrayerRequestsFlashing = useIsPrayerRequestsLinkFlashing();
  const isHomePageFlashing = useIsHomePageLinkFlashing();
  const isMinistryToolsSquiggleFlashing = useIsMinistryToolsSquiggleFlashing();

  useEffect(() => {
    if (!initialized) {
      initialize(initialUnseenTotal);
    }
  }, [initialized, initialUnseenTotal, initialize]);

  const firstVisibleLink = useMemo(() => {
    if (canSeePrayerPortalNav) return "/prayerportal";
    if (canSeeReportsMenuNav) return "/reports";
    if (canManageHomePage || canSeeMMGSetup) return "/homepage";
    return null;
  }, [canSeePrayerPortalNav, canSeeReportsMenuNav, canManageHomePage, canSeeMMGSetup]);

  return (
    <SNavSection
      $isActive={isActive}
      label="Ministry Tools"
      data-cy="NavBar_MinistryToolsMenu"
      navSectionKey={navSectionKeys.tools}
      color={navColors.tools}
      icon="screwdriver-wrench far"
      count={unseenTotal}
      headerTextRef={ministryToolsRef}
      isSquiggleFlashing={isMinistryToolsSquiggleFlashing}
      defaultRoute={firstVisibleLink}
    >
      <NavLinksWrapper>
        {canSeePrayerPortalNav && (
          <NavLink
            to="prayerportal"
            data-cy="NavBar_PrayerRequests"
            icon="praying-hands fad"
            count={unseenTotal}
            navSectionKey={navSectionKeys.tools}
            showCount
            ref={prayerRequestsLinkRef}
            isFlashing={isPrayerRequestsFlashing}
          >
            Prayer Requests
          </NavLink>
        )}
        {canSeeReportsMenuNav && (
          <NavLink
            navSectionKey={navSectionKeys.tools}
            to="reports"
            data-cy="NavBar_ReportBuilder"
            icon="table fad"
            ref={reportBuilderLinkRef}
            isFlashing={isReportBuilderFlashing}
          >
            Report Builder
          </NavLink>
        )}
        {(canManageHomePage || canSeeMMGSetup) && (
          <NavLink
            to="homepage"
            data-cy="NavBar_PortalSettings"
            icon="hammer-brush fad fa-swap-opacity"
            count={unseenTotal}
            navSectionKey={navSectionKeys.tools}
            ref={homePageLinkRef}
            isFlashing={isHomePageFlashing}
          >
            Home Page
          </NavLink>
        )}
      </NavLinksWrapper>
    </SNavSection>
  );
};

const SNavSection = styled(NavSection)`
  padding-bottom: ${({ $isActive }) => ($isActive ? "4px" : "0")};
  transition: padding-bottom 300ms;
`;

export default MinistryTools;
